import type { ConsumptionInsightsSeason } from "~/api/dto/proposal-consumption.dto"

export const SEASONS: ConsumptionInsightsSeason[] = [
  `winter`,
  `spring`,
  `summer`,
  `fall`,
]

/**
 * Locked categorical palette from Consumption Insights spec (#4494). The
 * values live in CSS so the drawer keeps the same charts in the Battery route
 * theme; `global.css` restates each token for a dark surface.
 */
export const SEASON_COLORS: Record<ConsumptionInsightsSeason, string> = {
  winter: `var(--consumption-season-winter)`,
  spring: `var(--consumption-season-spring)`,
  summer: `var(--consumption-season-summer)`,
  fall: `var(--consumption-season-fall)`,
}

export const CONSUMPTION_COLOR = `var(--consumption-series)`
export const PRODUCTION_COLOR = `var(--consumption-production)`

export const SEASON_LABELS: Record<ConsumptionInsightsSeason, string> = {
  winter: `Winter`,
  spring: `Spring`,
  summer: `Summer`,
  fall: `Fall`,
}

export const AXIS_COLOR = `var(--consumption-axis)`
export const GRID_COLOR = `var(--consumption-grid)`
export const LABEL_COLOR = `var(--consumption-label)`
export const EVENING_BAND_FILL = `var(--consumption-evening-band)`
export const EVENING_BAND_LABEL = `var(--consumption-evening-band-label)`
export const HEATMAP_GAP_FILL = GRID_COLOR
export const HEATMAP_IMPUTED_TICK = EVENING_BAND_FILL

/** Fixed 4pm–9pm window: hour starts 16–20 inclusive; band ends at 21:00. */
export const EVENING_WINDOW = { startHour: 16, endHourExclusive: 21 } as const

export const axisLabelProps = {
  fill: LABEL_COLOR,
  fontFamily: `Barlow, sans-serif`,
  fontSize: 11,
} as const

export const formatHour = (hour: number): string => {
  if (hour === 0 || hour === 24) return `12am`
  if (hour === 12) return `12pm`
  return hour < 12 ? `${hour}am` : `${hour - 12}pm`
}

export const formatKw = (value: number): string =>
  `${new Intl.NumberFormat(`en-US`, { maximumFractionDigits: 1 }).format(value)} kW`

export const formatKwh = (value: number): string =>
  `${new Intl.NumberFormat(`en-US`, { maximumFractionDigits: 2 }).format(value)} kWh`

/**
 * Sequential blue ramp for the Usage patterns heatmap, running from the
 * quietest hour to the busiest. Mixing the two CSS endpoints keeps the ramp
 * pointing the legible direction in whichever route theme the drawer opens in.
 */
export const heatmapRampColor = (t: number): string => {
  const percent = Math.round(Math.max(0, Math.min(1, t)) * 100)
  return `color-mix(in srgb, var(--consumption-ramp-to) ${percent}%, var(--consumption-ramp-from))`
}
